import axios from 'axios'
import store from '@/store'

const API_BASE_URL = process.env.VUE_APP_BACKEND_URL || 'http://localhost:8080'

// Detecta se está usando ngrok pela URL
const isUsingNgrok = API_BASE_URL.includes('ngrok')

// Headers base
const baseHeaders = {
  'Content-Type': 'application/json',
}

// Adiciona header do ngrok APENAS se a URL contiver "ngrok"
if (isUsingNgrok) {
  baseHeaders['ngrok-skip-browser-warning'] = 'true'
}

// Cria instância do Axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: baseHeaders,
  validateStatus: function (status) {
    // Aceita qualquer status 2xx como sucesso (200, 201, 204, etc)
    return status >= 200 && status < 300
  },
})

// Interceptor para adicionar token em todas as requisições
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('firebaseToken')
    const tenantId = localStorage.getItem('tenantId')
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId
    }
    
    // ===== MODO VISUALIZAÇÃO: Adicionar header com ID do gestor =====
    // Quando TENANT_ADMIN está visualizando dados de um gestor específico,
    // o backend deve filtrar/retornar dados como se fosse aquele gestor
    const viewOnlyMode = store.getters.isViewOnlyMode
    const managerId = store.getters.viewOnlyManagerId
    
    if (viewOnlyMode && managerId) {
      config.headers['X-View-As-Manager-Id'] = managerId
      console.log(`🔍 Modo Visualização: Carregando dados como gestor ${managerId}`)
      
      // ===== BLOQUEIO DE ESCRITA EM MODO VISUALIZAÇÃO =====
      // Bloqueia POST/PUT/PATCH/DELETE para garantir que o admin não
      // altere dados do gestor. Whitelist para operações seguras de leitura.
      const method = (config.method || '').toLowerCase()
      const url = config.url || ''
      const isWriteMethod = ['post', 'put', 'patch', 'delete'].includes(method)
      
      if (isWriteMethod) {
        // Whitelist: operações que NÃO alteram dados do gestor
        const safeEndpoints = [
          '/api/notifications/',        // marcar notificações como lidas
          '/api/notifications/read-all', // marcar todas como lidas
        ]
        const isSafe = safeEndpoints.some(ep => url.includes(ep))
        
        if (!isSafe) {
          console.warn(`🚫 Modo Visualização: Bloqueando ${method.toUpperCase()} ${url}`)
          store.commit('setSnackbar', {
            show: true,
            color: 'warning',
            text: 'Modo visualização — não é possível realizar alterações'
          })
          return Promise.reject(new axios.Cancel('Operação bloqueada em modo visualização'))
        }
      }
    }
    
    // Adiciona header ngrok APENAS se a URL contiver "ngrok"
    if (isUsingNgrok) {
      config.headers['ngrok-skip-browser-warning'] = 'true'
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para tratar erros globalmente
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Em modo visualização, 401 pode ser erro do viewAs, não token expirado
      // Não deslogar nesse caso
      const isViewOnly = store.getters.isViewOnlyMode
      if (isViewOnly) {
        console.warn('⚠️ Erro 401 em modo visualização — não deslogando. Verifique X-View-As-Manager-Id no backend.')
        return Promise.reject(error)
      }

      // 401 de endpoints de integração (Microsoft, Jira, GitHub) = token da integração expirou,
      // NÃO o token do usuário. Não deslogar.
      const url = error.config?.url || ''
      const isIntegrationEndpoint = url.includes('/integrations/microsoft/') ||
        url.includes('/integrations/jira/') ||
        url.includes('/integrations/github/')
      if (isIntegrationEndpoint) {
        console.warn('⚠️ Erro 401 em endpoint de integração — não deslogando:', url)
        return Promise.reject(error)
      }
      
      // Token expirado ou inválido — deslogar
      localStorage.removeItem('firebaseToken')
      localStorage.removeItem('user')
      localStorage.removeItem('tenantId')
      window.location.href = '/login'
    }
    
    return Promise.reject(error)
  }
)

export default apiClient
